import * as React from "react";
import {
  Plus,
  Image as ImageIcon,
  FileText,
  Box,
  Brain,
  Globe,
  Crosshair,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../primitives/popover";
import { cn } from "../../lib/cn";
import type { ComposerAttachKind, ComposerCapability } from "./types";

/**
 * Composer.AttachButton — `+` overflow inside the chip bar.
 *
 * Opens a popover with two sections:
 *   - Allega: Immagine / File (PDF/DOCX/XLSX/…) / Disegno CAD
 *   - Capacità AI: Thinking, Web search, Focus (Standard/Strutturati/Full), CAD analysis
 *
 * State (capability active/inactive, sub-label) is owned by the parent —
 * the popover just emits events. Mirrors v1 `QueryInput.tsx` tools popover
 * but split out so it can be reused (e.g. inline AI toolbar).
 */
export interface ComposerAttachButtonProps {
  capabilities?: ComposerCapability[];
  onAttach?: (kind: ComposerAttachKind) => void;
  onCapabilityToggle?: (id: ComposerCapability["id"]) => void;
  /** Localised labels — defaults are Italian per v18. */
  labels?: Partial<{
    trigger: string;
    sectionAttach: string;
    sectionCapabilities: string;
    image: string;
    file: string;
    cad: string;
  }>;
  className?: string;
}

const DEFAULT_LABELS = {
  trigger: "Allega o attiva capacità AI",
  sectionAttach: "Allega",
  sectionCapabilities: "Capacità AI",
  image: "Immagine",
  file: "File",
  cad: "Disegno CAD",
} as const;

const CAPABILITY_ICONS: Record<
  ComposerCapability["id"],
  React.ComponentType<{ className?: string; size?: number }>
> = {
  thinking: Brain,
  web: Globe,
  focus: Crosshair,
  cad: Box,
};

export const ComposerAttachButton = React.forwardRef<
  HTMLButtonElement,
  ComposerAttachButtonProps
>(
  (
    { capabilities = [], onAttach, onCapabilityToggle, labels, className },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);
    const L = { ...DEFAULT_LABELS, ...labels };

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            ref={ref}
            type="button"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={L.trigger}
            title={L.trigger}
            className={cn(
              "inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg",
              "cursor-pointer border-none bg-transparent text-ink-3",
              "transition-[color,background-color,transform] duration-200 ease-smooth",
              "hover:bg-black/[0.045] hover:text-ink-1",
              "active:scale-[0.92]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-offset-1",
              open && "bg-ando-orange/[0.12] text-ando-orange",
              className
            )}
          >
            <Plus size={15} strokeWidth={1.85} aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          side="top"
          sideOffset={8}
          className="w-[300px] p-1.5"
        >
          <section aria-label={L.sectionAttach}>
            <div className="px-2 pt-1 pb-1 font-medium text-[10px] text-ink-3 uppercase tracking-wide">
              {L.sectionAttach}
            </div>
            <AttachRow
              icon={ImageIcon}
              label={L.image}
              onSelect={() => {
                setOpen(false);
                onAttach?.("image");
              }}
            />
            <AttachRow
              icon={FileText}
              label={L.file}
              onSelect={() => {
                setOpen(false);
                onAttach?.("file");
              }}
            />
            <AttachRow
              icon={Box}
              label={L.cad}
              onSelect={() => {
                setOpen(false);
                onAttach?.("cad");
              }}
            />
          </section>
          {capabilities.length > 0 ? (
            <>
              <div className="my-1 h-px bg-hairline" />
              <section aria-label={L.sectionCapabilities}>
                <div className="px-2 pt-1 pb-1 font-medium text-[10px] text-ink-3 uppercase tracking-wide">
                  {L.sectionCapabilities}
                </div>
                {capabilities.map((cap) => {
                  const Icon = CAPABILITY_ICONS[cap.id];
                  return (
                    <CapabilityRow
                      key={cap.id}
                      icon={Icon}
                      label={cap.label}
                      activeLabel={cap.activeLabel}
                      active={cap.active}
                      onToggle={() => onCapabilityToggle?.(cap.id)}
                    />
                  );
                })}
              </section>
            </>
          ) : null}
        </PopoverContent>
      </Popover>
    );
  }
);
ComposerAttachButton.displayName = "Composer.AttachButton";

// ─────────────────────────────────────────────────────────────────────────
// Internal row components — kept colocated so AttachButton stays self-contained.
// ─────────────────────────────────────────────────────────────────────────

interface RowProps {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
}

function AttachRow({ icon: Icon, label, onSelect }: RowProps & { onSelect: () => void }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-ink-2 text-xs",
        "transition-colors duration-150 ease-out",
        "hover:bg-black/[0.04] hover:text-ink-1",
        "focus-visible:bg-black/[0.04] focus-visible:text-ink-1 focus-visible:outline-none"
      )}
    >
      <Icon size={14} className="shrink-0 text-ink-3" />
      <span className="truncate">{label}</span>
    </button>
  );
}

function CapabilityRow({
  icon: Icon,
  label,
  activeLabel,
  active,
  onToggle,
}: RowProps & {
  activeLabel?: string;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={active}
      onClick={onToggle}
      className={cn(
        "flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs",
        "transition-colors duration-150 ease-out",
        active ? "bg-ando-orange/[0.10] text-ink-1" : "text-ink-2 hover:bg-black/[0.04] hover:text-ink-1"
      )}
    >
      <span className="flex min-w-0 items-center gap-2">
        <Icon
          size={14}
          className={cn("shrink-0", active ? "text-ando-orange" : "text-ink-3")}
        />
        <span className="truncate">
          {label}
          {active && activeLabel ? (
            <span className="ml-1 font-normal text-ink-3">· {activeLabel}</span>
          ) : null}
        </span>
      </span>
      <span
        className={cn(
          "inline-flex h-3.5 w-3.5 items-center justify-center rounded-full",
          active
            ? "bg-ando-orange text-white"
            : "border border-hairline text-transparent"
        )}
        aria-hidden="true"
      >
        {active ? (
          <svg
            width="7"
            height="7"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            aria-hidden="true"
            focusable="false"
          >
            <title>Selezionato</title>
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : null}
      </span>
    </button>
  );
}
